/**
 * 页面卸载时上报被中断的上传。
 *
 * 上传状态只存在于发起上传那个标签页的内存 store 里（zustand，无持久化），
 * 因此「本标签页 store 中存在 queued/uploading 的 entry」就是「本标签页是上传发起者」
 * 的可靠信号；其他用户的标签页没有这些 entry，刷新/关闭不会影响上传状态。
 *
 * 必须用 fetch + keepalive：axios/XHR 不支持 keepalive，sendBeacon 不能带
 * Authorization 且只支持 POST。
 */

import { API_BASE_URL, getAccessToken } from '@/api/client';
import { useUploadProgressStore, type FileProgressEntry } from '@/store/uploadProgressStore';

export type AbandonTarget = 'file' | 'release_note' | 'test_report';

export interface ParsedProgressKey {
  deliverableType: 'package' | 'external_release';
  deliverableId: number;
  target: AbandonTarget;
  fileId?: number;
}

export interface AbandonRequest {
  key: string;
  url: string;
  body: Record<string, unknown>;
}

const KEY_PATTERN = /^(package|externalrelease)-(\d+)-(?:file-(\d+)|doc-(release_note|test_report)(?:-(\d+))?)$/;

const DOC_PATH: Record<'release_note' | 'test_report', string> = {
  release_note: 'release-note',
  test_report: 'test-report',
};

export const parseProgressKey = (key: string): ParsedProgressKey | null => {
  const match = KEY_PATTERN.exec(key);
  if (!match) return null;

  const [, prefix, deliverableId, fileId, docType] = match;
  const target: AbandonTarget = fileId ? 'file' : (docType as 'release_note' | 'test_report');
  return {
    deliverableType: prefix === 'package' ? 'package' : 'external_release',
    deliverableId: Number(deliverableId),
    target,
    ...(fileId ? { fileId: Number(fileId) } : {}),
  };
};

export const buildAbandonRequests = (
  progress: Record<string, FileProgressEntry>,
): AbandonRequest[] => {
  const requests: AbandonRequest[] = [];
  // 多附件文档的每个文件有独立进度 key，failing 只需打一次
  const seenDocs = new Set<string>();

  for (const [key, entry] of Object.entries(progress)) {
    if (!entry || (entry.status !== 'queued' && entry.status !== 'uploading')) continue;

    const parsed = parseProgressKey(key);
    if (!parsed) continue;

    const collection = parsed.deliverableType === 'package' ? 'packages' : 'external-releases';
    const base = `${API_BASE_URL}/${collection}/${parsed.deliverableId}`;

    if (parsed.target === 'file') {
      requests.push({
        key,
        url: `${base}/files/${parsed.fileId}/upload-complete/`,
        body: { upload_status: 'failed' },
      });
    } else {
      const docKey = `${parsed.deliverableType}-${parsed.deliverableId}-${parsed.target}`;
      if (seenDocs.has(docKey)) continue;
      seenDocs.add(docKey);
      requests.push({ key, url: `${base}/${DOC_PATH[parsed.target]}/fail/`, body: {} });
    }
  }

  return requests;
};

let installed = false;
let removeListener: (() => void) | null = null;
const reported = new Set<string>();

/** 幂等注册；返回清理函数（测试用）。 */
export const installUploadUnloadGuard = (): (() => void) => {
  if (installed) return removeListener ?? (() => {});
  if (typeof window === 'undefined') return () => {};

  const handler = () => {
    try {
      const token = getAccessToken();
      if (!token) return;

      const requests = buildAbandonRequests(useUploadProgressStore.getState().progress);
      if (requests.length === 0) return;

      const send = globalThis.fetch;
      if (typeof send !== 'function') return;

      for (const request of requests) {
        if (reported.has(request.key)) continue;
        reported.add(request.key);
        send(request.url, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(request.body),
          keepalive: true,
        }).catch(() => {});
      }
    } catch {
      // 卸载路径绝不能抛异常
    }
  };

  window.addEventListener('pagehide', handler);
  installed = true;

  removeListener = () => {
    window.removeEventListener('pagehide', handler);
    installed = false;
    reported.clear();
    removeListener = null;
  };

  return removeListener;
};
